<script lang="ts">
	import type { HistorySnapshot } from '#lib/history/types';
	import { i18n, t } from '#lib/i18n/state.svelte';
	import { scoreTone } from '#lib/ui/labels';

	interface Props {
		snapshots: HistorySnapshot[];
		onselect: (snapshot: HistorySnapshot) => void;
		onclose: () => void;
	}
	let { snapshots, onselect, onclose }: Props = $props();

	function when(iso: string): string {
		try {
			return new Date(iso).toLocaleString(i18n.lang === 'tr' ? 'tr-TR' : 'en-US', {
				year: 'numeric',
				month: 'short',
				day: '2-digit',
				hour: '2-digit',
				minute: '2-digit'
			});
		} catch {
			return iso;
		}
	}
</script>

<div class="overlay">
	<div
		class="modal"
		role="dialog"
		tabindex="-1"
		aria-modal="true"
		aria-label={t('history.title')}
	>
		<header class="head">
			<div>
				<p class="eyebrow">{t('history.eyebrow')}</p>
				<h2>{t('history.title')}</h2>
				<p class="sub">{t('history.sub')}</p>
			</div>
			<button class="btn btn-ghost btn-sm" onclick={onclose} aria-label={t('common.close')}>✕</button>
		</header>

		{#if snapshots.length === 0}
			<p class="empty">{t('history.empty')}</p>
		{:else}
			<ul class="list">
				{#each snapshots as snap (snap.id)}
					<li>
						<button class="row" onclick={() => onselect(snap)}>
							<span class="file">{snap.fileName}</span>
							<span class="date">{when(snap.date)}</span>
							<span class="score" style:color={scoreTone(snap.score)}>
								{snap.score}<span class="grade">{snap.grade}</span>
							</span>
							<span class="count">{t('history.issues', { n: snap.issueCount })}</span>
						</button>
					</li>
				{/each}
			</ul>
		{/if}
	</div>
</div>

<style>
	.overlay {
		position: fixed;
		inset: 0;
		background: rgb(28 25 23 / 0.42);
		display: grid;
		place-items: center;
		padding: 1rem;
		z-index: 60;
	}
	.modal {
		width: min(38rem, 100%);
		max-height: min(34rem, 88vh);
		overflow: auto;
		background: var(--paper);
		border: 1px solid var(--line);
		border-radius: var(--radius);
		box-shadow: var(--shadow-lg, 0 24px 60px rgb(0 0 0 / 0.2));
		padding: 1.2rem;
	}
	.head {
		display: flex;
		justify-content: space-between;
		gap: 1rem;
		align-items: flex-start;
	}
	.eyebrow {
		margin: 0 0 0.2rem;
		font-size: 0.68rem;
		text-transform: uppercase;
		letter-spacing: 0.1em;
		color: var(--muted);
		font-weight: 650;
	}
	h2 {
		margin: 0 0 0.2rem;
		font-size: 1.15rem;
	}
	.sub {
		margin: 0;
		color: var(--muted);
		font-size: 0.82rem;
	}
	.empty {
		margin: 1.4rem 0 0.4rem;
		color: var(--ink-2);
	}
	.list {
		list-style: none;
		padding: 0;
		margin: 1rem 0 0;
		display: grid;
		gap: 0.4rem;
	}
	.row {
		width: 100%;
		text-align: left;
		display: grid;
		grid-template-columns: 1fr auto;
		gap: 0.15rem 0.7rem;
		padding: 0.7rem 0.8rem;
		border: 1px solid var(--line);
		border-radius: 10px;
		background: #fff;
		cursor: pointer;
		font: inherit;
	}
	.row:hover {
		border-color: var(--line-strong);
		background: var(--paper-2);
	}
	.file {
		font-weight: 600;
		font-size: 0.88rem;
		overflow-wrap: anywhere;
	}
	.date {
		grid-column: 1;
		font-size: 0.74rem;
		color: var(--muted);
	}
	.score {
		grid-row: 1 / span 2;
		grid-column: 2;
		font-family: var(--font-display);
		font-size: 1.35rem;
		align-self: center;
	}
	.grade {
		font-family: var(--font-ui);
		font-size: 0.75rem;
		margin-left: 0.15rem;
	}
	.count {
		grid-column: 1;
		font-size: 0.72rem;
		color: var(--muted);
	}
</style>
